/** @jsxImportSource @emotion/react */

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import { Avatar, IconButton } from '@mui/material'
import { useQuery } from '@apollo/client'
import { endOfDay, isAfter } from 'date-fns'
import ButtonGroup from '@mui/material/ButtonGroup'
import Button from '@mui/material/Button'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import Table, { FooterWrapper, PaginationPage, PaginationTake } from 'components/Table'
import { Search } from 'components/Input'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import Select from 'components/Select'
import { GET_PLATFORMS } from 'queries/common'
import { findAllPlatforms, findAllPlatformsVariables } from '__generatedTypes__/findAllPlatforms'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import Tooltip from 'components/Tooltip'
import { PlatformName, RolesEnum } from '__generatedTypes__/globalTypes'
import { PageLoader } from 'components/PageLoader'
import { MEMBERSHIP_TYPES } from 'pagesNew/Users'
import { TooltipTitle } from 'pagesNew/Users/List/components/TooltipTitle'
import DateTimeInput from 'components/DateTimeInput'
import { getFullPlatformTitle } from 'components/Layout/Sidebar/PlatformList'
import { findAllUsersAccountSharing_findAllUsersAccountSharing_items } from '__generatedTypes__/findAllUsersAccountSharing'
import { findUsersCountByRoles } from '__generatedTypes__/findUsersCountByRoles'
import { GET_USERS_COUNT_BY_ROLES } from 'queries/users'
import { useAccountSharing } from './useAccountSharing'
import * as classes from './styles'
import { BlockButton } from './components/BlockButton'

export const AccountSharing = () => {
  const [error, setError] = useState<string | null>(null)
  const { getLink } = useLink()

  const { data: rolesCount } = useQuery<findUsersCountByRoles>(GET_USERS_COUNT_BY_ROLES, {
    fetchPolicy: 'cache-and-network',
  })

  const { data: platformsData, loading: platformsLoading } = useQuery<findAllPlatforms, findAllPlatformsVariables>(
    GET_PLATFORMS,
    {
      variables: { query: { take: TAKE_ALL_ITEMS_COUNT } },
    }
  )

  const accountSharingState = useAccountSharing()
  const { items, count, loading, refetch, pagination, search, filters, order, onFilterChange } = accountSharingState

  useEffect(() => {
    refetch()
  }, [refetch])

  const platformItems = platformsData?.findAllPlatforms.items || []

  const platforms = useMemo(
    () =>
      platformItems.map((platform) => ({
        id: platform.title,
        title: platform.title,
      })) || [],
    [platformItems]
  )

  const isValidDateTo = useMemo(() => {
    if (filters.dateFrom && filters.dateTo) {
      return isAfter(filters.dateTo, filters.dateFrom)
    }
    return true
  }, [filters.dateFrom, filters.dateTo])

  const handleEndDateChange = (value: Date | null) => {
    const endOfTheDay = value ? endOfDay(value ?? new Date()) : null

    onFilterChange('dateTo', endOfTheDay)
  }

  const handleBlockUserRefetch = () => {
    if (filters.isBlocked) {
      refetch()
    }
  }

  const tabFilters = [
    {
      title: 'All',
      count: rolesCount?.findUsersCountByRoles.all ?? 0,
      isBlocked: false,
    },
    {
      title: 'Blocked',
      count: rolesCount?.findUsersCountByRoles.blocked ?? 0,
      isBlocked: true,
    },
  ]

  if (!accountSharingState.loaded) {
    return <PageLoader />
  }

  return (
    <section css={classes.container}>
      <div css={classes.header}>
        <Typography css={classes.title} variant="title3">
          Account Sharing
        </Typography>
      </div>
      <div>
        <ButtonGroup css={classes.buttonGroup}>
          {tabFilters.map(({ title, count, isBlocked }) => (
            <Button
              key={title}
              variant="text"
              css={classes.isBlockedFilterButton(!!filters.isBlocked === isBlocked)}
              onClick={() => onFilterChange('isBlocked', isBlocked)}
            >
              {title} ({count || 0})
            </Button>
          ))}
        </ButtonGroup>
      </div>
      <div css={classes.toolbarWrapper}>
        <div css={classes.filterInputs}>
          <Search {...search} css={classes.search} />
          <Select
            css={classes.platformsSelect}
            items={platforms}
            value={filters.selectedPlatform || null}
            onChange={(value) => onFilterChange('selectedPlatform', value ?? undefined)}
            renderItem={(platform) => getFullPlatformTitle(platform.title as PlatformName)}
            getItemLabel={(platform) => getFullPlatformTitle(platform.title as PlatformName) ?? platform.title}
            placeholder="Active Platform"
            loading={platformsLoading}
            displaySelectedItemsFirst
          />
          <div css={classes.dateRange}>
            <DateTimeInput
              css={classes.dateInput}
              placeholder="Start date"
              pickerType="date"
              format="MM/dd/yyyy"
              withClearIcon
              value={filters.dateFrom ?? null}
              onChange={(value) => onFilterChange('dateFrom', value ?? undefined)}
            />
            <DateTimeInput
              css={classes.dateInput}
              placeholder="End date"
              pickerType="date"
              format="MM/dd/yyyy"
              withClearIcon
              value={filters.dateTo ?? null}
              onChange={handleEndDateChange}
              error={!isValidDateTo}
            />
          </div>
          <DateTimeInput
            css={classes.dateInput}
            onChange={(value) => onFilterChange('membershipDaysRemaining', value ?? undefined)}
            placeholder="Expiration Date"
            pickerType="date"
            format="MM/dd/yyyy"
            withClearIcon
            value={filters.membershipDaysRemaining ?? null}
          />
        </div>
      </div>
      {error && (
        <div css={classes.errorWrapper}>
          <Typography css={classes.error} variant="body2">
            {error}
          </Typography>
        </div>
      )}
      <Table
        css={classes.tableWrapper}
        columns={[
          {
            title: 'Full Name',
            renderCell: (accountSharingItem) => (
              <div css={classes.titleWrapper}>
                <div css={classes.fullNameCell}>
                  <Avatar src={accountSharingItem?.avatar ?? undefined} alt="Avatar" />
                  {accountSharingItem.firstName} {accountSharingItem.lastName}
                </div>
              </div>
            ),
            flex: 3,
          },
          {
            title: 'Email',
            renderCell: (accountSharingItem) => <div css={classes.emailCell}>{accountSharingItem.email}</div>,
            flex: 3,
          },
          {
            title: 'Purchases',
            renderCell: (accountSharingItem) => (
              <div css={[classes.titleWrapper, classes.membershipsCell]}>
                {accountSharingItem.role === RolesEnum.SuperAdmin
                  ? '-'
                  : MEMBERSHIP_TYPES.map(({ type, title, key, css }) => {
                      const purchases = accountSharingItem.purchases[key] || 0

                      return (
                        <Tooltip
                          key={key}
                          title={
                            <TooltipTitle
                              purchases={accountSharingItem.purchases}
                              type={type}
                              title={`${title} Memberships`}
                            />
                          }
                          arrow
                          placement="left"
                          {...(purchases === 0 ? { open: false } : {})}
                        >
                          <span css={css}>
                            {title}({purchases})
                          </span>
                        </Tooltip>
                      )
                    })}
              </div>
            ),
            flex: '0 1 120px',
          },
          {
            title: '# of Regions',
            renderCell: (accountSharingItem) => (
              <div css={classes.titleWrapper}>{accountSharingItem.countOfRegion}</div>
            ),
            flex: '0 1 100px',
            orderField: 'countOfRegions',
          },
          {
            title: '# of Browser ID',
            renderCell: (accountSharingItem) => (
              <div css={classes.titleWrapper}>{accountSharingItem.countOfBrowserId}</div>
            ),
            flex: '0 1 120px',
            orderField: 'countOfBrowserIds',
          },
          {
            title: '',
            renderCell: (accountSharingItem) =>
              accountSharingItem.role === RolesEnum.SuperAdmin ? (
                ''
              ) : (
                <div css={classes.actions}>
                  <Link to={getLink(cmsRoutes.USER_EDIT, { userId: accountSharingItem.id })}>
                    <IconButton size="small" css={classes.action} disabled={loading}>
                      <EditIcon />
                    </IconButton>
                  </Link>
                  <BlockButton
                    accountSharingItem={accountSharingItem}
                    setError={setError}
                    refetch={handleBlockUserRefetch}
                  />
                </div>
              ),
            flex: '0 1 142px',
          },
        ]}
        items={items as findAllUsersAccountSharing_findAllUsersAccountSharing_items[]}
        onOrder={order.onChange}
        order={order.value}
        wrapperMinWidth={1010}
      />
      <FooterWrapper>
        <PaginationTake {...pagination} />
        <PaginationPage totalCount={count ?? 0} {...pagination} />
      </FooterWrapper>
    </section>
  )
}
